import { User, Bot, Clock } from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { ChatMessage as ChatMessageType } from "../types";
import useUser from "context/hooks/user";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import { t } from "i18next";

const ChatMessage: React.FC<{
  message: ChatMessageType;
  showTimestamp?: boolean;
}> = ({ message, showTimestamp = true }) => {
  const { data: userDetail }: { data: any } = useUser();
  const { dir } = useAppSelector(getSettings);

  const formatTime = (timestamp: Date | string) => {
    try {
      // Create a date object if timestamp is a string
      const date =
        typeof timestamp === "string" ? new Date(timestamp) : timestamp;
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting time:", error, timestamp);
      return ""; // Return empty string if error occurs
    }
  };

  // Function to preserve newlines but not disturb list formatting
  const preserveNewlines = (text: string): string => {
    // Don't add breaks if it looks like a list
    if (/^\s*\d+\.\s+/gm.test(text)) {
      return text;
    }
    return text.replace(/\n/g, "  \n");
  };

  return (
    <div
      className={`flex ${
        message.sender === "user" ? "justify-end" : "justify-start"
      } animate-fade-in max-w-full`}
    >
      <div
        className={`flex max-w-[85%] sm:max-w-[80%] ${
          message.sender === "user"
            ? "bg-primary text-black rounded-2xl rounded-tr-sm"
            : "bg-zinc-100 text-zinc-800 rounded-2xl rounded-tl-sm"
        } px-3 py-2 sm:px-4 sm:py-3 text-sm shadow-sm`}
      dir={dir}

      >
        <div className="flex-1">
          <div className="inline-flex items-center mb-1">
            {message.sender === "user" ? (
              <User className="w-3 h-3 mr-1" />
            ) : (
              <Bot className="w-3 h-3 mr-1" />
            )}
            <span className="text-xs opacity-75">
              {message.sender === "user" ? userDetail?.first_name : t("chatbotAssistant")}
            </span>
            {showTimestamp && message.timestamp && (
              <span className="ml-2 text-xs opacity-75 flex items-center">
                <Clock className="w-2.5 h-2.5 mr-1" />&nbsp;
                {formatTime(message.timestamp)}
              </span>
            )}
          </div>
          <div className="markdown-content text-xs sm:text-sm">
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm]}
              components={{
                ol: ({ node, ...props }) => (
                  <ol className="list-decimal" {...props} />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc" {...props} />
                ),
                li: ({ node, ...props }) => <li className="my-1" {...props} />,
                a: ({ node, href, ...props }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    {...props}
                  />
                ),
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-2">
                    <table
                      className="min-w-full border border-collapse border-gray-300"
                      {...props}
                    />
                  </div>
                ),
                thead: ({ node, ...props }) => (
                  <thead className="bg-gray-100" {...props} />
                ),
                th: ({ node, ...props }) => (
                  <th
                    className="border border-gray-300 px-3 py-2 text-left font-medium"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td className="border border-gray-300 px-3 py-2" {...props} />
                ),
                tr: ({ node, ...props }) => (
                  <tr className="border-b border-gray-300" {...props} />
                ),
              }}
            >
              {preserveNewlines(message.content)}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
